import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Loader2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScribPostLogo } from "@/components/scribpost-logo";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

export default function SignupPage() {
  const capturedEmail = useAppStore((s) => s.capturedEmail);
  const { sessionId, displayName, loginUser, setCapturedEmail, completeOnboarding } = useAppStore();
  const [name, setName] = useState(displayName ?? "");
  const [email, setEmail] = useState(capturedEmail ?? "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      setCapturedEmail(email.trim());
      const res = await apiFetch(`${baseUrl}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          password,
          name: name.trim() || null,
          sessionId,
          username: username.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Signup failed");
        return;
      }

      loginUser({
        token: data.token,
        userId: data.user.id,
        email: data.user.email,
        emailVerified: data.user.emailVerified,
        canonicalSessionId: data.user.canonicalSessionId,
        displayName: data.user.displayName,
        subscriptionStatus: data.user.subscriptionStatus ?? "none",
        subscriptionPlan: data.user.subscriptionPlan ?? null,
      });
      completeOnboarding();

      toast({
        title: "Account created!",
        description: "Welcome to ScribPost. Check your email to verify your account.",
      });
      setLocation("/");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground relative overflow-hidden px-4">
      <div className="bg-orb-1" aria-hidden="true" />
      <div className="bg-orb-2" aria-hidden="true" />
      <div className="bg-orb-3" aria-hidden="true" />
      <div className="bg-grid" aria-hidden="true" />
      <div className="bg-aurora" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl shadow-primary/10 p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <ScribPostLogo size="lg" />
            </div>
            <h1 className="font-display font-bold text-2xl">Create your account</h1>
            <p className="text-sm text-muted-foreground">
              Start free — 15 actions, no credit card required.
            </p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
              >
                {error}
              </motion.div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signup-name">
                Your name <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Input
                id="signup-name"
                type="text"
                placeholder="Jane Smith"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 rounded-xl bg-background border-border"
                autoComplete="name"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signup-email">
                Email
              </label>
              <Input
                id="signup-email"
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl bg-background border-border"
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signup-username">
                Username
              </label>
              <Input
                id="signup-username"
                type="text"
                placeholder="janewrites (4+ chars)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12 rounded-xl bg-background border-border"
                autoComplete="username"
                minLength={4}
                maxLength={30}
                pattern="[a-zA-Z0-9_]+"
                title="Letters, numbers, and underscores only"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signup-password">
                Password
              </label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password (8+ chars)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl bg-background border-border pr-12"
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full h-12 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
              disabled={loading || !email.trim() || !username.trim() || password.length < 8}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground">or</span>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
              Log in →
            </Link>
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
          <Link href="/privacy">
            <span className="hover:text-foreground transition-colors cursor-pointer">Privacy Policy</span>
          </Link>
          <span>&middot;</span>
          <Link href="/terms">
            <span className="hover:text-foreground transition-colors cursor-pointer">Terms of Service</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
